import useAuthStore from "../../../../stores/useAuthStore";
import usePOSConfigStore from "../../../../stores/usePOSConfigStore";
import useCartStore from "../../../../stores/useCartStore";
import useTransactionStore from "../../../../stores/useTransactionStore";
import { enrichLineItemFields } from "../utils/taxCalculations";

/**
 * Shared engine for the per-line price rules (PA, TPA, TBP). `resolveTotal(rule, line, qty, realPrice)`
 * returns what the line should cost for `qty` units, or null when the rule does not apply to it.
 */
const runPriceAdjustment = async (
  resolveTotal,
  product,
  pricingRule,
  setCart,
  cart,
  orderType,
  cartObj,
  iscoupon,
  couponInput,
  uniqReferenceId,
  mPricingCouponId,
  mPricingRulesId,
  removeDiscounts
) => {
  const isExchange = useTransactionStore.getState().selectedReturnType === "EXCHANGE";
  const saleTypeValidation = pricingRule?.cwrSaletypeId === null || orderType === undefined ? true : pricingRule?.cwrSaletypeId === orderType?.cwrSaletype?.cwrSaletypeId;
  const tillData = useAuthStore.getState().tillData;
  const saleTypeData = usePOSConfigStore.getState().saleTypeData || {};
  let taxIncludeFlag = saleTypeData?.customer?.istaxincluded === "Y" ? "Y" : tillData.tillAccess.csBunit.isTaxIncluded ? tillData.tillAccess.csBunit.isTaxIncluded : "Y";
  // Check if there are any changes
  let hasChanges = false;

  // Iterate through each item in the cart
  cartObj.items.forEach((item) => {
    let excludingFlag = true;

    const matchingCategory = pricingRule.mPricingPcategories.find((pc) => pc.mProductCategoryId === item.mProductCategoryId);
    if (
      (pricingRule.excludeProductCategories === "Y" && matchingCategory) ||
      (pricingRule.excludeProductCategories !== "Y" && !matchingCategory && pricingRule.mPricingPcategories.length > 0) ||
      (pricingRule.excludeProductCategories === "N" && pricingRule.mPricingPcategories.length === 0)
    ) {
      excludingFlag = false;
    }

    const matchingBrand = pricingRule.mPricingBrands.find((pc) => pc.mBrandId === item.productBrandId);
    if (
      (pricingRule.excludeBrands === "Y" && matchingBrand) ||
      (pricingRule.excludeBrands !== "Y" && !matchingBrand && pricingRule.mPricingBrands.length > 0) ||
      (pricingRule.excludeBrands === "N" && pricingRule.mPricingBrands.length === 0)
    ) {
      excludingFlag = false;
    }

    const matchingProductIndex = pricingRule.mPricingXProducts.findIndex((op) => op.mProductId === item.productId);
    if (
      (pricingRule.excludeProducts === "Y" && matchingProductIndex !== -1) ||
      (pricingRule.excludeProducts !== "Y" && matchingProductIndex === -1 && pricingRule.mPricingXProducts.length > 0) ||
      (pricingRule.excludeProducts === "N" && pricingRule.mPricingXProducts.length === 0)
    ) {
      excludingFlag = false;
    }

    let pricingRuleFlag = true;

    if (item.discount <= 0) {
      // Check for product ID match before deleting priority
      const matchingProduct = pricingRule.mPricingXProducts.find((res) => res.mProductId === item.productId);
      if (matchingProduct) {
        // console.debug("Deleting priority for item:", item.productId);
        delete item.priority;
      }
    }

    if (
      saleTypeValidation &&
      excludingFlag &&
      pricingRuleFlag &&
      (product.productId === item.productId || iscoupon || removeDiscounts) &&
      (item?.priority ? item?.priority < pricingRule.priority : true)
    ) {
      const index = cartObj.items.findIndex((p) => p.productId === item.productId && p.upc === item.upc && p.mBatchId === item.mBatchId);

      if (index >= 0) {
        const line = cartObj.items[index];
        const prcPrecision = tillData.tillAccess.csBunit.currencies[0].prcPrecision;
        const realPrice = Math.abs(parseFloat(line.realPrice) || 0);
        const qty = Math.abs(line.weight);
        const resolvedTotal = resolveTotal(pricingRule, line, qty, realPrice);

        if (resolvedTotal !== null) {
          // Exchange lines carry a negative weight: keep the sign on the money, not on the unit price.
          const sign = line.weight < 0 ? -1 : 1;
          const sp = qty > 0 ? resolvedTotal / qty : 0;
          const mrp = resolvedTotal * sign;
          const tax = taxIncludeFlag === "Y" ? mrp - mrp / (1 + line.taxRate / 100) : (mrp * line.taxRate) / 100;
          const discount = parseFloat((realPrice * qty - resolvedTotal).toFixed(prcPrecision));

          // Check if any changes are made
          if (sp !== line.salePrice || tax !== line.taxAmount || mrp !== line.nettotal || discount !== line.discount) {
            if (line.productId === product.productId) {
              hasChanges = true;
            }
          }

          line.salePrice = sp;
          line.taxAmount = parseFloat(tax.toFixed(prcPrecision));
          line.nettotal = taxIncludeFlag === "Y" ? parseFloat(mrp.toFixed(prcPrecision)) : parseFloat((mrp + tax).toFixed(prcPrecision));
          line.discount = discount;
          line.discountName = pricingRule.printedName;
          line.mPricingruleId = pricingRule.mPricingrulesId;
          line.nextRule = pricingRule.nextRule;
          line.iscoupon = iscoupon;
          line.couponInput = couponInput;
          line.referenceId = uniqReferenceId;
          line.priority = pricingRule.priority;
          enrichLineItemFields(line, taxIncludeFlag, prcPrecision);
        } else if (line.mPricingruleId === pricingRule.mPricingrulesId) {
          // This rule priced the line before but no longer applies (qty dropped below the minimum or
          // the tier): put the line back to its regular price. A line priced by another rule, or not
          // discounted at all, is left alone so the controller can still try the next rule.
          const mrp = parseFloat(line.realPrice) * line.weight;
          const tax = taxIncludeFlag === "Y" ? mrp - mrp / (1 + line.taxRate / 100) : (mrp * line.taxRate) / 100;

          if (line.productId === product.productId) {
            hasChanges = true;
          }

          line.salePrice = parseFloat(line.realPrice);
          line.taxAmount = parseFloat(tax.toFixed(prcPrecision));
          line.nettotal = taxIncludeFlag === "Y" ? parseFloat(mrp.toFixed(prcPrecision)) : parseFloat((mrp + tax).toFixed(prcPrecision));
          line.discount = 0;
          line.discountName = "";
          line.mPricingruleId = null;
          delete line.nextRule;
          delete line.priority;
          enrichLineItemFields(line, taxIncludeFlag, prcPrecision);
        }
      }
    }
  });

  // Check if any changes are made and return the updated cart object
  if (hasChanges) {
    let couponsData = cart.couponInput?.length > 0 ? [...cart.couponInput] : [];
    if (!couponsData.some((coupon) => coupon.couponCode === couponInput) && couponInput !== undefined && couponInput !== null) {
      couponsData.push({ couponCode: couponInput, referenceId: uniqReferenceId, mPricingCouponId: mPricingCouponId, mPricingruleId: mPricingRulesId });
    }
    cartObj.iscoupon = iscoupon;
    cartObj.couponInput = couponsData;
    if (isExchange) {
      useCartStore.getState().setExchangeCart(cartObj);
    } else {
      setCart(cartObj);
    }
    return cartObj;
  }
};

export const createPriceAdjustmentHandler =
  (resolveTotal) =>
  (...args) =>
    runPriceAdjustment(resolveTotal, ...args);

/**
 * POS-01 + G2: the price this rule charges for `qty` units of `line`, or null when the rule does not
 * apply to the line. Never returns a total that is free or dearer than the regular price.
 *  1. Header fixedUnitPrice > 0: that price per unit (old behaviour), once qty reaches minimumQty.
 *  2. Otherwise the product's own row in mPricingXProducts (price > 0), the row with the largest
 *     quantity the line reaches:
 *       - quantity 1: price per unit;
 *       - quantity N > 1: price is for a bundle of N. Full bundles are charged at that price and the
 *         leftover units at the regular price, e.g. 2 for ₹90 (MRP ₹50) on qty 5 = 2 × 90 + 1 × 50.
 *  3. Neither: null. Before this, a rule without fixedUnitPrice priced every line at ₹0.
 * minimumQty is read into a local; it used to be written back onto the shared rule object.
 */
export const resolveAdjustedTotal = (pricingRule, line, qty, realPrice) => {
  const minQty = Number(pricingRule.minimumQty) > 0 ? Number(pricingRule.minimumQty) : 0;
  if (!(qty > 0) || qty < minQty) return null;

  let total = null;
  const fixedUnitPrice = Number(pricingRule.fixedUnitPrice);
  if (fixedUnitPrice > 0) {
    total = fixedUnitPrice * qty;
  } else {
    const rows = pricedRowsForLine(pricingRule, line).filter((row) => (Number(row.quantity) || 1) <= qty);
    if (rows.length === 0) return null;
    const best = rows.reduce((a, b) => ((Number(b.quantity) || 1) > (Number(a.quantity) || 1) ? b : a));
    const bundleQty = Number(best.quantity) || 1;
    const price = Number(best.price);
    if (bundleQty <= 1) {
      total = price * qty;
    } else {
      const bundles = Math.floor(qty / bundleQty);
      total = bundles * price + (qty - bundles * bundleQty) * realPrice;
    }
  }

  // A price adjustment only ever lowers the price; at or above the regular price it does not apply.
  if (!(total >= 0) || total >= realPrice * qty) return null;
  return total;
};

/** Rows of this rule that price `line`: same product (and batch when the row names one), price > 0. */
export const pricedRowsForLine = (pricingRule, line) =>
  (pricingRule.mPricingXProducts || []).filter(
    (row) => row.mProductId === line.productId && (!row.mBatchId || row.mBatchId === line.mBatchId) && Number(row.price) > 0
  );

export const priceAdjustment = createPriceAdjustmentHandler(resolveAdjustedTotal);
